// Dedicated SEO landing pages content (HU / EN / DE).
// Slugs are shared across languages to keep routing simple.

export const PAGE_ORDER = ["stage1", "stage2", "stage3", "dyno", "partner"];

export const PAGE_SLUGS = {
  stage1: "stage-1-chiptuning",
  stage2: "stage-2-chiptuning",
  stage3: "stage-3-chiptuning",
  dyno: "dyno-pro",
  partner: "autotuner-flex-partner",
};

export const SLUG_TO_KEY = Object.fromEntries(
  Object.entries(PAGE_SLUGS).map(([k, v]) => [v, k])
);

export const PAGE_IMAGES = {
  stage1:
    "https://images.pexels.com/photos/11455717/pexels-photo-11455717.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
  stage2:
    "https://images.pexels.com/photos/38570/lamborghini-car-speed-prestige-38570.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
  stage3:
    "https://images.unsplash.com/photo-1713212727531-51e120bd416a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  dyno:
    "https://images.unsplash.com/photo-1713212727531-51e120bd416a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  partner:
    "https://images.pexels.com/photos/3807517/pexels-photo-3807517.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
};

export const PAGES = {
  hu: {
    common: { home: "Főoldal", cta: "Fájl igénylése", related: "Kapcsolódó szolgáltatások", specs: "Amit kapsz", back: "Vissza a főoldalra" },
    stage1: {
      slug: PAGE_SLUGS.stage1,
      meta: {
        title: "Stage 1 Chiptuning Fájl | Gyári hardveren maximum — TuningFiles.hu",
        description: "Stage 1 chiptuning fájl gyári hardverre: +20-30% teljesítmény, tisztább gázreakció, alacsonyabb fogyasztás. Egyedi kalibráció, német minőség, aznapi leszállítás.",
      },
      kicker: "STAGE 1 · CHIPTUNING FÁJL",
      h1: "Stage 1 chiptuning fájl",
      lead: "A Stage 1 a legnépszerűbb és legbiztonságosabb tuning: a gyári hardveren rejlő teljesítmény maximumát hozzuk ki egyedi, kézzel optimalizált szoftverrel — kockázat nélkül.",
      highlights: [
        { title: "+20-30% teljesítmény", text: "Érezhető nyomatéknövekedés már alacsony fordulaton, gyári megbízhatóság mellett." },
        { title: "Alacsonyabb fogyasztás", text: "Optimalizált égés a takarékosabb, nyugodt vezetéshez (eco tuning)." },
        { title: "Gyors OBD flash", text: "A legtöbb autó OBD porton keresztül, hardverbontás nélkül programozható." },
        { title: "Aznapi leszállítás", text: "Átlagos átfutás kevesebb mint 2 óra — a műhelyed nem áll meg." },
      ],
      body: [
        { h: "Mi az a Stage 1 tuning?", p: "A Stage 1 chiptuning során az autó gyári ECU szoftverét optimalizáljuk: befecskendezés, feltöltőnyomás és gyújtás finomhangolásával a motor tartalékait használjuk ki. Nincs szükség hardveres módosításra — ezért a Stage 1 a legjobb ár-érték arányú belépő a chiptuning világába." },
        { h: "Kinek ajánljuk?", p: "Ideális minden műhelynek és tunernek, aki gyors, biztonságos és bevételes szolgáltatást szeretne kínálni ügyfeleinek. Minden fájl egyedi, a konkrét motorra és vezérlőre szabva — nem generikus térkép." },
      ],
      included: ["Egyedi, kézzel kalibrált fájl", "Gyári tűréshatárokon belül", "Visszaállítható gyári állapot", "DPF/EGR/AdBlue opciók kérésre", "Teljes technikai támogatás"],
      faq: [
        { q: "Biztonságos a Stage 1?", a: "Igen, minden térkép a motor gyári tűréshatárain belül marad, így a megbízhatóság megőrződik." },
        { q: "Kell hozzá hardver?", a: "Nem, a Stage 1 gyári hardveren fut, a legtöbb esetben OBD flash elegendő." },
        { q: "Mennyi idő?", a: "A legtöbb Stage 1 fájlt aznap, átlagosan 2 órán belül leszállítjuk." },
      ],
      related: ["stage2", "dyno", "partner"],
    },
    stage2: {
      slug: PAGE_SLUGS.stage2,
      meta: {
        title: "Stage 2 Chiptuning Fájl | Hardver-optimalizált tuning — TuningFiles.hu",
        description: "Stage 2 chiptuning fájl hardveres módosításokhoz: letöltőcső, intercooler, sportszűrő. Egyedi kalibráció a maximális kihasználtságért, német minőségben.",
      },
      kicker: "STAGE 2 · CHIPTUNING FÁJL",
      h1: "Stage 2 chiptuning fájl",
      lead: "A Stage 2 a hardveres módosításokhoz hangolt szoftver: ha letöltőcső, sport intercooler vagy nagyobb átömlésű szűrő került az autóba, a Stage 2 hozza ki belőle a maximumot.",
      highlights: [
        { title: "Hardverre hangolva", text: "A fájl a beépített módosításokhoz igazodik a biztonságos többletért." },
        { title: "Sport kipufogó ready", text: "Downpipe és sportkipufogó melletti optimális hangolás." },
        { title: "Egyedi kalibráció", text: "Minden fájl az adott build alapján készül, nem sablon." },
        { title: "Dokumentált eredmény", text: "Kérésre fékpados igazolás a valós teljesítményről." },
      ],
      body: [
        { h: "Mikor kell Stage 2?", p: "Amikor az autó már túllépett a gyári hardver korlátain: nagyobb átömlésű letöltőcső, sport intercooler vagy szűrő esetén a szoftvert is ehhez kell igazítani. A Stage 2 fájl kihasználja ezeket a módosításokat a biztonságos és megbízható többletteljesítményért." },
        { h: "Miért nálunk?", p: "Minden Stage 2 fájlt a konkrét összeállításra szabunk. A német mérnöki alaposság és a fékpados validálás garantálja, hogy a motor a határokon belül, mégis a csúcson teljesítsen." },
      ],
      included: ["Hardverre szabott kalibráció", "Downpipe / intercooler támogatás", "Opcionális fékpados validálás", "Biztonságos, tartós beállítás", "Technikai konzultáció"],
      faq: [
        { q: "Milyen hardver kell?", a: "Tipikusan letöltőcső, sport intercooler vagy nagyobb szűrő — ezekhez hangoljuk a fájlt." },
        { q: "Kombinálható a Dyno Pro-val?", a: "Igen, javasoljuk a fékpados validálást a dokumentált eredményért." },
        { q: "Megmarad a megbízhatóság?", a: "Igen, a beállítás a motor tűréshatárain belül marad." },
      ],
      related: ["stage1", "stage3", "dyno"],
    },
    stage3: {
      slug: PAGE_SLUGS.stage3,
      meta: {
        title: "Stage 3 Chiptuning Fájl | Verseny szintű tuning — TuningFiles.hu",
        description: "Stage 3 chiptuning fájl turbó- és motormódosításokhoz. Motorsport szintű teljesítmény, hibrid turbó támogatás, egyedi projektfájlok német precizitással.",
      },
      kicker: "STAGE 3 · CHIPTUNING FÁJL",
      h1: "Stage 3 chiptuning fájl",
      lead: "A Stage 3 a határok feszegetése: verseny szintű, turbó- és belsőmotor-módosításokhoz készült egyedi projektfájlok, teljes kontrollal és dokumentációval.",
      highlights: [
        { title: "Motorsport szint", text: "Maximális teljesítmény komoly hardveres alapokra építve." },
        { title: "Hibrid turbó support", text: "Nagyobb vagy hibrid turbó melletti egyedi kalibráció." },
        { title: "Egyedi projekt", text: "Minden Stage 3 build önálló mérnöki feladat, egyedi kísérettel." },
        { title: "Fékpados finomhangolás", text: "Valós méréssel, iterációkkal a stabil végeredményért." },
      ],
      body: [
        { h: "Kinek való a Stage 3?", p: "Komoly buildeknek: hibrid vagy nagyobb turbó, befecskendező, hűtés és belsőmotor-módosítások esetén. A Stage 3 nem sablon — minden esetben egyedi projekt, ahol a biztonság és a teljesítmény egyensúlyát mi garantáljuk." },
        { h: "Hogyan zajlik?", p: "Felmérjük a hardvert, egyedi alaptérképet készítünk, majd fékpadon iterálunk a stabil, ismételhető teljesítményig. A végén dokumentált görbét és beállítást kapsz." },
      ],
      included: ["Egyedi projektfájl", "Hibrid turbó kalibráció", "Fékpados iteráció", "Dokumentált teljesítménygörbe", "Dedikált technikai kíséret"],
      faq: [
        { q: "Sablon vagy egyedi?", a: "Kizárólag egyedi — a Stage 3 mindig projekt alapú, a konkrét hardverre." },
        { q: "Kell fékpad?", a: "Erősen ajánlott a biztonságos, ismételhető eredményhez." },
        { q: "Mennyi idő?", a: "A build komplexitásától függ; egyeztetünk a projekt indításakor." },
      ],
      related: ["stage2", "dyno", "partner"],
    },
    dyno: {
      slug: PAGE_SLUGS.dyno,
      meta: {
        title: "Dyno Pro — Fékpados Validált Fájlok | TuningFiles.hu",
        description: "Dyno Pro: fékpadon (dyno) validált chiptuning fájlok. Valós mérés, dokumentált teljesítménygörbe, bizonyítható eredmény az ügyfélnek. Német minőség.",
      },
      kicker: "DYNO PRO · FÉKPADOS FÁJLOK",
      h1: "Dyno Pro — fékpados validált fájlok",
      lead: "A Dyno Pro fájljainkat fékpadon validáljuk. Valós méréssel dokumentált teljesítménygörbét adunk, hogy az eredmény ne ígéret, hanem bizonyíték legyen.",
      highlights: [
        { title: "Fékpad validálva", text: "Minden fájl valós méréssel ellenőrizve, nem csak elmélet." },
        { title: "Görbe dokumentáció", text: "Le/fel görbe, nyomaték és teljesítmény pontos adatokkal." },
        { title: "Bizonyítható eredmény", text: "Az ügyfélnek átadható, hiteles teljesítménylap." },
        { title: "Opcionális DPF/EGR", text: "Igény szerinti kiegészítő megoldások szabályos keretek közt." },
      ],
      body: [
        { h: "Miért fontos a fékpad?", p: "A fékpad (dyno) az egyetlen mód a valós teljesítmény pontos mérésére. A Dyno Pro fájloknál nem becslünk: mérünk, iterálunk és dokumentálunk, így az ügyfél fekete-fehéren látja, mit kapott." },
        { h: "Mit tartalmaz?", p: "A Dyno Pro csomag tartalmazza a validált fájlt és a dokumentált teljesítménygörbét. Ez erős értékesítési eszköz a műhelyed kezében: bizalom, ami visszatérő ügyfeleket hoz." },
      ],
      included: ["Fékpadon validált fájl", "Dokumentált teljesítménygörbe", "Nyomaték- és teljesítményadatok", "Ügyfélnek átadható lap", "Opcionális kiegészítők"],
      faq: [
        { q: "Kell hozzá saját fékpad?", a: "Nem feltétlenül; partnereinkkel megoldjuk a validálást." },
        { q: "Milyen adatokat kapok?", a: "Teljesítmény- és nyomatékgörbét, valós mérési adatokkal." },
        { q: "Kombinálható Stage 2/3-mal?", a: "Igen, ez a leggyakoribb és leghitelesebb kombináció." },
      ],
      related: ["stage2", "stage3", "partner"],
    },
    partner: {
      slug: PAGE_SLUGS.partner,
      meta: {
        title: "Autotuner Flex & Slave Partnerprogram | Csatlakozz — TuningFiles.hu",
        description: "Legyél tuningfiles.hu partner: Autotuner Flex & Slave támogatás, prioritásos fájlleszállítás, nagykereskedelmi árak és white-label megoldás műhelyeknek és tunereknek.",
      },
      kicker: "AUTOTUNER FLEX / SLAVE · PARTNERPROGRAM",
      h1: "Autotuner Flex & Slave partnerprogram",
      lead: "Csatlakozz partnerhálózatunkhoz műhelyként vagy tunerként. Teljes Autotuner Flex és Slave támogatást, gyors fájlleszállítást és technikai hátteret adunk — hogy Te a munkára koncentrálhass.",
      highlights: [
        { title: "Prioritásos leszállítás", text: "Partnerként a Te fájljaid elöl vannak, 24/7 support-tal." },
        { title: "Flex & Slave support", text: "Teljes körű Autotuner Flex és Slave technikai támogatás." },
        { title: "Partnerárak", text: "Nagykereskedelmi árak és jutalék a bővülő forgalomért." },
        { title: "White-label", text: "A saját márkád alatt kínálhatod a szolgáltatást." },
      ],
      body: [
        { h: "Miért érdemes partnernek lenni?", p: "A chiptuning kiszervezése a fájloldalon időt és kockázatot spórol. Mi adjuk a szoftvert és a technikai hátteret, Te pedig magasabb árréssel, gyorsabban szolgálod ki az ügyfeleidet — anélkül, hogy drága szoftverbe és tudásba kellene fektetned." },
        { h: "Hogyan kezdjünk?", p: "Írj nekünk WhatsApp-on vagy emailben. Néhány kérdés után beállítjuk a hozzáférést, és már mehet is az első fájl. A partnerré válás gyors és kötelezettség nélküli." },
      ],
      included: ["Autotuner Flex & Slave támogatás", "Prioritásos fájlleszállítás", "Nagykereskedelmi partnerárak", "White-label megoldás", "Dedikált technikai kapcsolat"],
      faq: [
        { q: "Kell Autotuner eszköz?", a: "A Flex/Slave munkafolyamathoz igen; ebben is segítünk a beállítással." },
        { q: "Van belépési díj?", a: "Nincs kötelezettség; a részleteket személyre szabva egyeztetjük." },
        { q: "Hogyan jelentkezem?", a: "WhatsApp vagy email — néhány percen belül indulhatunk." },
      ],
      related: ["stage1", "stage2", "dyno"],
    },
  },

  en: {
    common: { home: "Home", cta: "Request a file", related: "Related services", specs: "What you get", back: "Back to home" },
    stage1: {
      slug: PAGE_SLUGS.stage1,
      meta: {
        title: "Stage 1 Chiptuning File | Maximum on stock hardware — TuningFiles.hu",
        description: "Stage 1 chiptuning file for stock hardware: +20-30% power, cleaner throttle response, lower consumption. Custom calibration, German quality, same-day delivery.",
      },
      kicker: "STAGE 1 · CHIPTUNING FILE",
      h1: "Stage 1 chiptuning file",
      lead: "Stage 1 is the most popular and safest tuning: we extract the maximum from your stock hardware with custom, hand-optimized software — with zero risk.",
      highlights: [
        { title: "+20-30% power", text: "Noticeable torque low in the rev range, with stock reliability." },
        { title: "Lower consumption", text: "Optimized combustion for calmer, more economical driving (eco tuning)." },
        { title: "Fast OBD flash", text: "Most cars are programmed via the OBD port, no hardware removal." },
        { title: "Same-day delivery", text: "Average turnaround under 2 hours — your workshop keeps moving." },
      ],
      body: [
        { h: "What is Stage 1 tuning?", p: "In Stage 1 chiptuning we optimize the car's stock ECU software: fine-tuning injection, boost pressure and ignition to use the engine's reserves. No hardware mods needed — that's why Stage 1 is the best value entry into tuning." },
        { h: "Who is it for?", p: "Ideal for any workshop or tuner who wants to offer a fast, safe and profitable service. Every file is custom, tailored to the specific engine and ECU — never a generic map." },
      ],
      included: ["Custom, hand-calibrated file", "Within factory tolerances", "Reversible to stock", "DPF/EGR/AdBlue options on request", "Full technical support"],
      faq: [
        { q: "Is Stage 1 safe?", a: "Yes, every map stays within the engine's factory tolerances, so reliability is preserved." },
        { q: "Do I need hardware?", a: "No, Stage 1 runs on stock hardware; in most cases an OBD flash is enough." },
        { q: "How long?", a: "Most Stage 1 files are delivered same day, within ~2 hours on average." },
      ],
      related: ["stage2", "dyno", "partner"],
    },
    stage2: {
      slug: PAGE_SLUGS.stage2,
      meta: {
        title: "Stage 2 Chiptuning File | Hardware-optimized tuning — TuningFiles.hu",
        description: "Stage 2 chiptuning file for hardware mods: downpipe, intercooler, sport filter. Custom calibration for maximum exploitation, in German quality.",
      },
      kicker: "STAGE 2 · CHIPTUNING FILE",
      h1: "Stage 2 chiptuning file",
      lead: "Stage 2 is software tuned for hardware modifications: with a downpipe, sport intercooler or high-flow filter installed, Stage 2 extracts the maximum.",
      highlights: [
        { title: "Tuned to hardware", text: "The file adapts to installed mods for safe extra power." },
        { title: "Sport exhaust ready", text: "Optimal tuning alongside downpipe and sport exhaust." },
        { title: "Custom calibration", text: "Every file is built for your specific build, not a template." },
        { title: "Documented result", text: "Optional dyno proof of the real performance." },
      ],
      body: [
        { h: "When do you need Stage 2?", p: "When the car has surpassed stock hardware limits: a high-flow downpipe, sport intercooler or filter requires matching software. The Stage 2 file exploits these mods for safe, reliable extra performance." },
        { h: "Why us?", p: "Every Stage 2 file is tailored to your specific build. German thoroughness and dyno validation guarantee the engine performs at its peak while staying within limits." },
      ],
      included: ["Hardware-matched calibration", "Downpipe / intercooler support", "Optional dyno validation", "Safe, durable setup", "Technical consultation"],
      faq: [
        { q: "What hardware is needed?", a: "Typically a downpipe, sport intercooler or larger filter — we tune the file to these." },
        { q: "Combine with Dyno Pro?", a: "Yes, we recommend dyno validation for a documented result." },
        { q: "Is reliability kept?", a: "Yes, the setup stays within the engine's tolerances." },
      ],
      related: ["stage1", "stage3", "dyno"],
    },
    stage3: {
      slug: PAGE_SLUGS.stage3,
      meta: {
        title: "Stage 3 Chiptuning File | Race-grade tuning — TuningFiles.hu",
        description: "Stage 3 chiptuning file for turbo and engine mods. Motorsport-level performance, hybrid turbo support, custom project files with German precision.",
      },
      kicker: "STAGE 3 · CHIPTUNING FILE",
      h1: "Stage 3 chiptuning file",
      lead: "Stage 3 pushes the limits: custom project files for race-level turbo and internal engine modifications, with full control and documentation.",
      highlights: [
        { title: "Motorsport level", text: "Maximum performance built on serious hardware foundations." },
        { title: "Hybrid turbo support", text: "Custom calibration alongside larger or hybrid turbos." },
        { title: "Custom project", text: "Every Stage 3 build is a standalone engineering task." },
        { title: "Dyno fine-tuning", text: "Real measurement and iterations for a stable end result." },
      ],
      body: [
        { h: "Who is Stage 3 for?", p: "For serious builds: hybrid or larger turbos, injectors, cooling and internal engine mods. Stage 3 is never a template — always a custom project where we guarantee the balance of safety and performance." },
        { h: "How does it work?", p: "We assess the hardware, build a custom base map, then iterate on the dyno until performance is stable and repeatable. You receive a documented curve and setup." },
      ],
      included: ["Custom project file", "Hybrid turbo calibration", "Dyno iteration", "Documented power curve", "Dedicated technical support"],
      faq: [
        { q: "Template or custom?", a: "Exclusively custom — Stage 3 is always project-based for the specific hardware." },
        { q: "Is a dyno needed?", a: "Strongly recommended for a safe, repeatable result." },
        { q: "How long?", a: "Depends on build complexity; we align at project start." },
      ],
      related: ["stage2", "dyno", "partner"],
    },
    dyno: {
      slug: PAGE_SLUGS.dyno,
      meta: {
        title: "Dyno Pro — Dyno-validated files | TuningFiles.hu",
        description: "Dyno Pro: dyno-validated chiptuning files. Real measurement, documented power curve, provable result for your customer. German quality.",
      },
      kicker: "DYNO PRO · DYNO-VALIDATED FILES",
      h1: "Dyno Pro — dyno-validated files",
      lead: "Our Dyno Pro files are validated on a dyno. We provide a documented power curve from a real measurement, so the result is proof, not a promise.",
      highlights: [
        { title: "Dyno validated", text: "Every file verified by real measurement, not just theory." },
        { title: "Curve documentation", text: "Power and torque curves with precise data." },
        { title: "Provable result", text: "A credible performance sheet to hand to the customer." },
        { title: "Optional DPF/EGR", text: "Additional solutions on request within legal frameworks." },
      ],
      body: [
        { h: "Why is the dyno important?", p: "The dyno is the only way to accurately measure real performance. With Dyno Pro files we don't estimate: we measure, iterate and document, so the customer sees exactly what they got." },
        { h: "What's included?", p: "The Dyno Pro package includes the validated file and the documented power curve. It's a powerful sales tool for your workshop: trust that brings back returning customers." },
      ],
      included: ["Dyno-validated file", "Documented power curve", "Power & torque data", "Customer-ready sheet", "Optional add-ons"],
      faq: [
        { q: "Do I need my own dyno?", a: "Not necessarily; we arrange validation with our partners." },
        { q: "What data do I get?", a: "Power and torque curves with real measurement data." },
        { q: "Combine with Stage 2/3?", a: "Yes, this is the most common and credible combination." },
      ],
      related: ["stage2", "stage3", "partner"],
    },
    partner: {
      slug: PAGE_SLUGS.partner,
      meta: {
        title: "Autotuner Flex & Slave Partner Program | Join — TuningFiles.hu",
        description: "Become a tuningfiles.hu partner: Autotuner Flex & Slave support, priority file delivery, wholesale pricing and a white-label solution for workshops and tuners.",
      },
      kicker: "AUTOTUNER FLEX / SLAVE · PARTNER PROGRAM",
      h1: "Autotuner Flex & Slave partner program",
      lead: "Join our partner network as a workshop or tuner. We provide full Autotuner Flex and Slave support, fast file delivery and technical backing — so you can focus on the work.",
      highlights: [
        { title: "Priority delivery", text: "As a partner your files come first, with 24/7 support." },
        { title: "Flex & Slave support", text: "Full Autotuner Flex and Slave technical support." },
        { title: "Partner pricing", text: "Wholesale pricing and commission for growing volume." },
        { title: "White-label", text: "Offer the service under your own brand." },
      ],
      body: [
        { h: "Why become a partner?", p: "Outsourcing the file side of chiptuning saves time and risk. We provide the software and technical backing, while you serve customers faster with higher margins — without investing in expensive software and know-how." },
        { h: "How do we start?", p: "Message us on WhatsApp or email. After a few questions we set up your access and your first file can go. Becoming a partner is fast and non-binding." },
      ],
      included: ["Autotuner Flex & Slave support", "Priority file delivery", "Wholesale partner pricing", "White-label solution", "Dedicated technical contact"],
      faq: [
        { q: "Do I need an Autotuner tool?", a: "For the Flex/Slave workflow yes; we also help with the setup." },
        { q: "Is there an entry fee?", a: "No obligation; we align the details individually." },
        { q: "How do I apply?", a: "WhatsApp or email — we can start within minutes." },
      ],
      related: ["stage1", "stage2", "dyno"],
    },
  },

  de: {
    common: { home: "Startseite", cta: "File anfragen", related: "Verwandte Leistungen", specs: "Das bekommst du", back: "Zurück zur Startseite" },
    stage1: {
      slug: PAGE_SLUGS.stage1,
      meta: {
        title: "Stage 1 Chiptuning File | Maximum auf Serienhardware — TuningFiles.hu",
        description: "Stage 1 Chiptuning File für Serienhardware: +20-30% Leistung, sauberere Gasannahme, geringerer Verbrauch. Individuelle Kalibrierung, deutsche Qualität, Lieferung am selben Tag.",
      },
      kicker: "STAGE 1 · CHIPTUNING FILE",
      h1: "Stage 1 Chiptuning File",
      lead: "Stage 1 ist das beliebteste und sicherste Tuning: Wir holen das Maximum aus deiner Serienhardware mit individueller, handoptimierter Software — ohne Risiko.",
      highlights: [
        { title: "+20-30% Leistung", text: "Spürbares Drehmoment früh im Drehzahlband, bei Serienzuverlässigkeit." },
        { title: "Geringerer Verbrauch", text: "Optimierte Verbrennung für ruhigeres, sparsameres Fahren (Eco-Tuning)." },
        { title: "Schneller OBD-Flash", text: "Die meisten Fahrzeuge werden über den OBD-Port programmiert, ohne Ausbau." },
        { title: "Lieferung am selben Tag", text: "Durchschnittliche Bearbeitung unter 2 Stunden — deine Werkstatt läuft weiter." },
      ],
      body: [
        { h: "Was ist Stage 1 Tuning?", p: "Beim Stage 1 Chiptuning optimieren wir die Serien-ECU-Software: durch Feinabstimmung von Einspritzung, Ladedruck und Zündung nutzen wir die Reserven des Motors. Keine Hardware-Mods nötig — deshalb ist Stage 1 der beste Einstieg ins Tuning." },
        { h: "Für wen ist es?", p: "Ideal für jede Werkstatt und jeden Tuner, der einen schnellen, sicheren und profitablen Service bieten will. Jedes File ist individuell, auf den konkreten Motor und das Steuergerät zugeschnitten — nie eine generische Map." },
      ],
      included: ["Individuelles, handkalibriertes File", "Innerhalb der Werkstoleranzen", "Auf Serie rückrüstbar", "DPF/EGR/AdBlue-Optionen auf Anfrage", "Voller technischer Support"],
      faq: [
        { q: "Ist Stage 1 sicher?", a: "Ja, jede Map bleibt innerhalb der Werkstoleranzen des Motors, die Zuverlässigkeit bleibt erhalten." },
        { q: "Brauche ich Hardware?", a: "Nein, Stage 1 läuft auf Serienhardware; meist genügt ein OBD-Flash." },
        { q: "Wie lange?", a: "Die meisten Stage-1-Files liefern wir am selben Tag, im Schnitt in ~2 Stunden." },
      ],
      related: ["stage2", "dyno", "partner"],
    },
    stage2: {
      slug: PAGE_SLUGS.stage2,
      meta: {
        title: "Stage 2 Chiptuning File | Hardware-optimiertes Tuning — TuningFiles.hu",
        description: "Stage 2 Chiptuning File für Hardware-Mods: Downpipe, Ladeluftkühler, Sportfilter. Individuelle Kalibrierung für maximale Ausnutzung, in deutscher Qualität.",
      },
      kicker: "STAGE 2 · CHIPTUNING FILE",
      h1: "Stage 2 Chiptuning File",
      lead: "Stage 2 ist die auf Hardware-Modifikationen abgestimmte Software: mit Downpipe, Sport-Ladeluftkühler oder High-Flow-Filter holt Stage 2 das Maximum heraus.",
      highlights: [
        { title: "Auf Hardware abgestimmt", text: "Das File passt sich den verbauten Mods für sichere Mehrleistung an." },
        { title: "Sportauspuff ready", text: "Optimale Abstimmung mit Downpipe und Sportauspuff." },
        { title: "Individuelle Kalibrierung", text: "Jedes File für deinen konkreten Build, keine Vorlage." },
        { title: "Dokumentiertes Ergebnis", text: "Optionaler Prüfstandsnachweis der realen Leistung." },
      ],
      body: [
        { h: "Wann brauchst du Stage 2?", p: "Wenn das Auto die Grenzen der Serienhardware überschritten hat: eine High-Flow-Downpipe, ein Sport-Ladeluftkühler oder Filter erfordern passende Software. Das Stage-2-File nutzt diese Mods für sichere, zuverlässige Mehrleistung." },
        { h: "Warum wir?", p: "Jedes Stage-2-File wird auf deinen konkreten Build zugeschnitten. Deutsche Gründlichkeit und Prüfstandsvalidierung garantieren Höchstleistung innerhalb sicherer Grenzen." },
      ],
      included: ["Auf Hardware abgestimmte Kalibrierung", "Downpipe / Ladeluftkühler-Support", "Optionale Prüfstandsvalidierung", "Sicheres, dauerhaftes Setup", "Technische Beratung"],
      faq: [
        { q: "Welche Hardware wird benötigt?", a: "Typischerweise Downpipe, Sport-Ladeluftkühler oder größerer Filter — darauf stimmen wir das File ab." },
        { q: "Mit Dyno Pro kombinieren?", a: "Ja, wir empfehlen die Prüfstandsvalidierung für ein dokumentiertes Ergebnis." },
        { q: "Bleibt die Zuverlässigkeit?", a: "Ja, das Setup bleibt innerhalb der Motortoleranzen." },
      ],
      related: ["stage1", "stage3", "dyno"],
    },
    stage3: {
      slug: PAGE_SLUGS.stage3,
      meta: {
        title: "Stage 3 Chiptuning File | Tuning auf Rennsportniveau — TuningFiles.hu",
        description: "Stage 3 Chiptuning File für Turbo- und Motorumbauten. Leistung auf Motorsportniveau, Hybridturbo-Support, individuelle Projekt-Files mit deutscher Präzision.",
      },
      kicker: "STAGE 3 · CHIPTUNING FILE",
      h1: "Stage 3 Chiptuning File",
      lead: "Stage 3 lotet die Grenzen aus: individuelle Projekt-Files für Turbo- und Motorumbauten auf Rennsportniveau, mit voller Kontrolle und Dokumentation.",
      highlights: [
        { title: "Motorsport-Niveau", text: "Maximale Leistung auf einer ernsthaften Hardware-Basis." },
        { title: "Hybridturbo-Support", text: "Individuelle Kalibrierung mit größeren oder Hybridturbos." },
        { title: "Individuelles Projekt", text: "Jeder Stage-3-Build ist eine eigenständige Aufgabe." },
        { title: "Prüfstand-Feinabstimmung", text: "Echte Messung und Iterationen für ein stabiles Endergebnis." },
      ],
      body: [
        { h: "Für wen ist Stage 3?", p: "Für ernsthafte Builds: Hybrid- oder größere Turbos, Injektoren, Kühlung und Motorumbauten. Stage 3 ist nie eine Vorlage — immer ein individuelles Projekt, bei dem wir die Balance aus Sicherheit und Leistung garantieren." },
        { h: "Wie läuft es ab?", p: "Wir bewerten die Hardware, erstellen eine individuelle Basis-Map und iterieren auf dem Prüfstand, bis die Leistung stabil und wiederholbar ist. Du erhältst eine dokumentierte Kurve und ein Setup." },
      ],
      included: ["Individuelles Projekt-File", "Hybridturbo-Kalibrierung", "Prüfstand-Iteration", "Dokumentierte Leistungskurve", "Dedizierter technischer Support"],
      faq: [
        { q: "Vorlage oder individuell?", a: "Ausschließlich individuell — Stage 3 ist immer projektbasiert für die konkrete Hardware." },
        { q: "Ist ein Prüfstand nötig?", a: "Dringend empfohlen für ein sicheres, wiederholbares Ergebnis." },
        { q: "Wie lange?", a: "Abhängig von der Komplexität; wir stimmen uns zum Projektstart ab." },
      ],
      related: ["stage2", "dyno", "partner"],
    },
    dyno: {
      slug: PAGE_SLUGS.dyno,
      meta: {
        title: "Dyno Pro — Prüfstand-validierte Files | TuningFiles.hu",
        description: "Dyno Pro: auf dem Prüfstand validierte Chiptuning-Files. Echte Messung, dokumentierte Leistungskurve, nachweisbares Ergebnis für deinen Kunden. Deutsche Qualität.",
      },
      kicker: "DYNO PRO · PRÜFSTAND-FILES",
      h1: "Dyno Pro — Prüfstand-validierte Files",
      lead: "Unsere Dyno-Pro-Files werden auf dem Prüfstand validiert. Wir liefern eine dokumentierte Leistungskurve aus echter Messung — das Ergebnis ist Nachweis, kein Versprechen.",
      highlights: [
        { title: "Prüfstand validiert", text: "Jedes File durch echte Messung verifiziert, nicht nur Theorie." },
        { title: "Kurvendokumentation", text: "Leistungs- und Drehmomentkurven mit präzisen Daten." },
        { title: "Nachweisbares Ergebnis", text: "Ein glaubwürdiges Leistungsblatt für den Kunden." },
        { title: "Optional DPF/EGR", text: "Zusätzliche Lösungen auf Anfrage im gesetzlichen Rahmen." },
      ],
      body: [
        { h: "Warum ist der Prüfstand wichtig?", p: "Der Prüfstand ist die einzige Möglichkeit, echte Leistung genau zu messen. Bei Dyno-Pro-Files schätzen wir nicht: Wir messen, iterieren und dokumentieren, damit der Kunde genau sieht, was er bekommen hat." },
        { h: "Was ist enthalten?", p: "Das Dyno-Pro-Paket enthält das validierte File und die dokumentierte Leistungskurve. Ein starkes Verkaufsinstrument für deine Werkstatt: Vertrauen, das wiederkehrende Kunden bringt." },
      ],
      included: ["Prüfstand-validiertes File", "Dokumentierte Leistungskurve", "Leistungs- & Drehmomentdaten", "Kundenfertiges Blatt", "Optionale Add-ons"],
      faq: [
        { q: "Brauche ich einen eigenen Prüfstand?", a: "Nicht zwingend; wir organisieren die Validierung mit unseren Partnern." },
        { q: "Welche Daten bekomme ich?", a: "Leistungs- und Drehmomentkurven mit echten Messdaten." },
        { q: "Mit Stage 2/3 kombinieren?", a: "Ja, das ist die häufigste und glaubwürdigste Kombination." },
      ],
      related: ["stage2", "stage3", "partner"],
    },
    partner: {
      slug: PAGE_SLUGS.partner,
      meta: {
        title: "Autotuner Flex & Slave Partnerprogramm | Mitmachen — TuningFiles.hu",
        description: "Werde tuningfiles.hu Partner: Autotuner Flex & Slave Support, priorisierte Fileauslieferung, Großhandelspreise und White-Label-Lösung für Werkstätten und Tuner.",
      },
      kicker: "AUTOTUNER FLEX / SLAVE · PARTNERPROGRAMM",
      h1: "Autotuner Flex & Slave Partnerprogramm",
      lead: "Werde Teil unseres Partnernetzwerks als Werkstatt oder Tuner. Wir bieten vollen Autotuner Flex und Slave Support, schnelle Fileauslieferung und technische Unterstützung — damit du dich auf die Arbeit konzentrieren kannst.",
      highlights: [
        { title: "Priorisierte Lieferung", text: "Als Partner kommen deine Files zuerst, mit 24/7 Support." },
        { title: "Flex & Slave Support", text: "Voller Autotuner Flex und Slave technischer Support." },
        { title: "Partnerpreise", text: "Großhandelspreise und Provision für wachsendes Volumen." },
        { title: "White-Label", text: "Biete den Service unter deiner eigenen Marke an." },
      ],
      body: [
        { h: "Warum Partner werden?", p: "Die Auslagerung der File-Seite des Chiptunings spart Zeit und Risiko. Wir liefern Software und technische Unterstützung, während du Kunden schneller mit höheren Margen bedienst — ohne in teure Software und Know-how zu investieren." },
        { h: "Wie starten wir?", p: "Schreib uns auf WhatsApp oder per E-Mail. Nach ein paar Fragen richten wir deinen Zugang ein und dein erstes File kann raus. Partner zu werden ist schnell und unverbindlich." },
      ],
      included: ["Autotuner Flex & Slave Support", "Priorisierte Fileauslieferung", "Großhandels-Partnerpreise", "White-Label-Lösung", "Dedizierter technischer Kontakt"],
      faq: [
        { q: "Brauche ich ein Autotuner-Tool?", a: "Für den Flex/Slave-Workflow ja; wir helfen auch beim Setup." },
        { q: "Gibt es eine Aufnahmegebühr?", a: "Keine Verpflichtung; die Details stimmen wir individuell ab." },
        { q: "Wie bewerbe ich mich?", a: "WhatsApp oder E-Mail — wir können in Minuten starten." },
      ],
      related: ["stage1", "stage2", "dyno"],
    },
  },
};
